/* eslint-disable @typescript-eslint/ban-ts-comment */
"use client";

import { useState, useMemo, useEffect, useRef, type MouseEvent } from "react";
import Link from "@/components/Link";
import { useUser } from "@clerk/nextjs";
import { useNativeSignIn } from "@/hooks/useNativeSignIn";
import { sendGtagEvent } from "@/lib/client/gtag";
import { sendRedditPixelEvent } from "@/lib/client/redditPixel";
import isEqual from "lodash/isEqual";
import {
  InputLabel,
  Flex,
  Tooltip,
  TextInput,
  Button,
  ActionIcon,
  Combobox,
  ScrollArea,
  useCombobox,
  Box as MantineBox,
  Text as MantineText,
  UnstyledButton as MantineUnstyledButton,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconBell, IconCheck, IconShare, IconPencil } from "@tabler/icons-react";
import { useFiltersStore } from "@/stores/filtersStore";
import useSavedFilters from "@/hooks/useSavedFilters";
import useResetSearch from "@/hooks/useResetSearch";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";
import PlusIcon from "@/icons/Plus";
import BinIcon from "@/icons/Bin";
import RenameFilterModal from "@/components/RenameFilterModal";

import styles from "./styles.module.scss";

// PostgreSQL JSONB alphabetises object keys on output; sort keys before
// stringifying so signatures are stable across loadSavedFilters refreshes.
const stableStringify = (value: unknown): string =>
  JSON.stringify(value, (_, v) => {
    if (v && typeof v === "object" && !Array.isArray(v)) {
      return Object.fromEntries(
        Object.entries(v as Record<string, unknown>).sort(([a], [b]) => a.localeCompare(b))
      );
    }
    return v;
  });

const SavedInput = ({ maxSaved = 5 }: { maxSaved: number }) => {
  const { user } = useUser();
  const { openSignIn } = useNativeSignIn();
  const { saveNewFilter, updateSavedFilter, deleteSavedFilter, shareFilter, renameSavedFilter } =
    useSavedFilters();
  const [sharing, setSharing] = useState(false);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const { copied: linkCopied, copyToClipboard } = useCopyToClipboard({
    eventCategory: "search",
    eventAction: "click",
    eventLabel: "share_saved_filter",
    resetDelay: 2000,
  });

  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  const [showMaxWarning, setShowMaxWarning] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [pendingNewFilterSelect, setPendingNewFilterSelect] = useState(false);
  const combobox = useCombobox({
    opened: isOpen,
    onOpenedChange: setIsOpen,
    onDropdownClose: () => setIsOpen(false),
  });

  const activeFilters = useFiltersStore((state) => state.activeFilters);
  const searchText = useFiltersStore((state) => state.searchText);
  const savedFilters = useFiltersStore((state) => state.savedFilters);
  const savedFilterId = useFiltersStore((state) => state.savedFilterId);
  const excludeRelists = useFiltersStore((state) => state.excludeRelists);
  const excludeAuctions = useFiltersStore((state) => state.excludeAuctions);
  const daysBack = useFiltersStore((state) => state.daysBack);
  const isNewFilter = useFiltersStore((state) => state.isNewFilter);
  const {
    saveFilters: saveFiltersStore,
    selectSavedFilter: selectSavedFilterStore,
    handleDeleteSavedFilter: handleDeleteSavedFilterStore,
    setSavedFilterId: setSavedFilterIdStore,
  } = useFiltersStore((state) => state.actions);
  const resetSearch = useResetSearch();

  // Refs for values needed inside effects without triggering re-runs.
  // Without these, the PUT response → loadSavedFilters → new savedFilters array
  // → activeSearch useMemo recomputes → effect re-fires → infinite loop.
  const savedFiltersRef = useRef(savedFilters);
  savedFiltersRef.current = savedFilters;
  const saveNewFilterRef = useRef(saveNewFilter);
  saveNewFilterRef.current = saveNewFilter;
  const updateSavedFilterRef = useRef(updateSavedFilter);
  updateSavedFilterRef.current = updateSavedFilter;

  const activeSearch = useMemo(() => {
    if (savedFilterId) {
      return savedFilters?.find((filter: DBSavedFilter) => filter.id === savedFilterId);
    }

    if (activeFilters?.id) {
      return savedFilters?.find(
        // @ts-ignore - saved filters have an id property
        (filter: DBSavedFilter) => filter?.search_filter?.id === activeFilters.id
      );
    }
    return undefined;
  }, [activeFilters, savedFilterId, savedFilters]);
  const activeSearchRef = useRef(activeSearch);
  activeSearchRef.current = activeSearch;
  const isNewFilterRef = useRef(isNewFilter);
  isNewFilterRef.current = isNewFilter;
  const currentSearchSignature = useMemo(
    () =>
      stableStringify({
        filters: activeFilters,
        searchText,
        excludeRelists: excludeRelists || undefined,
        excludeAuctions: excludeAuctions || undefined,
        daysBack: daysBack || undefined,
      }),
    [activeFilters, searchText, excludeRelists, excludeAuctions, daysBack]
  );
  const [optimisticSavedSignature, setOptimisticSavedSignature] = useState<string | null>(null);

  const activeSavedSignature = useMemo(() => {
    const searchFilter = activeSearch?.search_filter;
    if (!searchFilter) {
      return null;
    }

    return stableStringify({
      filters: searchFilter.filters,
      searchText: searchFilter.searchText || "",
      excludeRelists: searchFilter.excludeRelists || undefined,
      excludeAuctions: searchFilter.excludeAuctions || undefined,
      daysBack: searchFilter.daysBack || undefined,
    });
  }, [activeSearch]);

  const hasUnsavedChanges =
    !!activeSearch &&
    !isNewFilter &&
    currentSearchSignature !== activeSavedSignature &&
    currentSearchSignature !== optimisticSavedSignature;

  useEffect(() => {
    if (currentSearchSignature === activeSavedSignature) {
      setOptimisticSavedSignature(null);
    }
  }, [activeSavedSignature, currentSearchSignature]);

  const handleShare = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!activeSearch || sharing) return;

    setSharing(true);
    try {
      const token = await shareFilter(activeSearch.id);
      if (token) {
        await copyToClipboard(`${window.location.origin}/search?share=${token}`);
      } else {
        notifications.show({
          title: "Couldn't create share link",
          message: "Please try again.",
          color: "red",
          autoClose: 4000,
        });
      }
    } finally {
      setSharing(false);
    }
  };

  const handleDelete = (id?: number) => {
    handleDeleteSavedFilterStore(id, deleteSavedFilter);

    if (showMaxWarning) {
      setShowMaxWarning(false);
    }
  };

  // update the filter if user selects a new option or changes toggles
  // only auto-saves for newly created filters; existing filters require explicit "SAVE CHANGES"
  useEffect(() => {
    if (!isNewFilterRef.current) return;
    const search = activeSearchRef.current;
    // @ts-ignore - saved filters have an id property
    if (search && activeFilters?.id === search?.search_filter?.id) {
      saveFiltersStore(
        search.search_filter.name,
        activeFilters.id,
        savedFiltersRef.current || [],
        saveNewFilterRef.current,
        updateSavedFilterRef.current
      );
    }
  }, [activeFilters, excludeRelists, excludeAuctions, daysBack, saveFiltersStore]);

  useEffect(() => {
    if (!isNewFilterRef.current) return;
    const search = activeSearchRef.current;
    if (isEqual(activeFilters, search?.search_filter.filters)) {
      if (searchText) {
        if (search?.search_filter.name && search?.search_filter.searchText !== searchText) {
          saveFiltersStore(
            search?.search_filter?.name,
            search.search_filter.id,
            savedFiltersRef.current || [],
            saveNewFilterRef.current,
            updateSavedFilterRef.current
          );
        }
        return;
      }
      return;
    }
  }, [searchText, activeFilters, saveFiltersStore]);

  const toggleCreating = (e?: MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation();
    combobox.closeDropdown();

    if (!user) {
      setSigningIn(true);
      void openSignIn();
      return;
    }

    if (savedFilters && savedFilters?.length >= maxSaved) {
      setShowMaxWarning(true);
      notifications.show({
        title: "Saved filter limit reached",
        message: `You've reached your limit of ${maxSaved} saved filters. Delete one to save another.`,
        color: "yellow",
        autoClose: 5000,
      });
      return;
    }

    setCreating(!creating);
  };

  const onNameChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
    setName(e.currentTarget.value);
  };

  const onSave = async () => {
    if (!name) {
      return;
    }

    setSaveError(null);

    if (savedFilters && savedFilters.length >= maxSaved) {
      setCreating(false);
      setShowMaxWarning(true);
      notifications.show({
        title: "Saved filter limit reached",
        message: `You've reached your limit of ${maxSaved} saved filters. Delete one to save another.`,
        color: "yellow",
        autoClose: 5000,
      });
      return;
    }

    try {
      await saveFiltersStore(name, undefined, savedFilters || [], saveNewFilter, updateSavedFilter);
      setName("");
      setCreating(false);
      setPendingNewFilterSelect(true);
      sendGtagEvent("click", "search", "save_filter");
      sendRedditPixelEvent("Custom", { customEventName: "SavedFilterCreated" });
    } catch (error) {
      setSaveError((error as Error).message || "Failed to save filter");
      console.error("Error saving filter:", error);
    }
  };

  // Once the newly created filter appears in savedFilters, set savedFilterId so it
  // persists across reloads while keeping isNewFilter=true for auto-save.
  useEffect(() => {
    if (!pendingNewFilterSelect) return;
    // activeFilters gains an `id` (timestamp) when a new filter is created
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const newFilterSearchId = (activeFilters as any)?.id;
    if (!newFilterSearchId) return;
    const newFilter = savedFilters?.find(
      (f: DBSavedFilter) => f.search_filter.id === newFilterSearchId
    );
    if (newFilter) {
      setSavedFilterIdStore(newFilter.id);
      setPendingNewFilterSelect(false);
    }
  }, [pendingNewFilterSelect, savedFilters, activeFilters, setSavedFilterIdStore]);

  const saveActiveChanges = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    const search = activeSearch;
    if (!search || !hasUnsavedChanges) {
      return;
    }

    setOptimisticSavedSignature(currentSearchSignature);
    saveFiltersStore(
      search.search_filter.name,
      search.search_filter.id,
      savedFilters || [],
      saveNewFilter,
      updateSavedFilter
    );
  };

  const toggleSelected = (id?: number) => {
    if (activeSearch?.search_filter.id == id) {
      resetSearch();
      return;
    }
    selectSavedFilterStore(id, false, savedFilters);
    const selectedFilter = savedFilters?.find(
      (f: DBSavedFilter) => f.search_filter.id?.toString() === id?.toString()
    );
    if (selectedFilter) {
      const sf = selectedFilter.search_filter;
      setOptimisticSavedSignature(
        stableStringify({
          filters: sf.filters,
          searchText: sf.searchText || "",
          excludeRelists: sf.excludeRelists || undefined,
          excludeAuctions: sf.excludeAuctions || undefined,
          daysBack: sf.daysBack || undefined,
        })
      );
    }
  };

  const toggleOpen = () => {
    if (!user) {
      setSigningIn(true);
      void openSignIn();
      return;
    }

    if (!savedFilters?.length) {
      return;
    }

    combobox.toggleDropdown("mouse");
  };

  return (
    <MantineBox className={styles.saveFilters}>
      {showMaxWarning && (
        <MantineText className={styles.notice}>
          {`YOU'VE REACHED YOUR LIMIT OF ${maxSaved} SAVED FILTERS. DELETE ONE TO SAVE ANOTHER.`}
        </MantineText>
      )}
      {saveError && (
        <MantineText className={styles.noticeError}>
          <MantineText span component="em">
            {saveError}
          </MantineText>
        </MantineText>
      )}
      <Flex w="100%" justify="space-between" align="center">
        <InputLabel>SAVED FILTERS</InputLabel>
        <Flex align="center" gap={10}>
          {activeSearch && (
            <>
              <Tooltip label="Rename this filter" position="top" withArrow>
                <MantineUnstyledButton
                  type="button"
                  className={styles.shareButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    setRenameModalOpen(true);
                  }}
                  aria-label={`Rename ${activeSearch.search_filter.name}`}
                >
                  <IconPencil size={18} color="#1500FF" style={{ cursor: "pointer" }} />
                </MantineUnstyledButton>
              </Tooltip>
              <Tooltip
                label={linkCopied ? "Link copied!" : "Share this filter"}
                position="top"
                withArrow
              >
                <MantineUnstyledButton
                  type="button"
                  className={styles.shareButton}
                  onClick={handleShare}
                  disabled={sharing}
                  aria-label={`Share ${activeSearch.search_filter.name}`}
                >
                  {linkCopied ? (
                    <IconCheck size={18} color="#1500FF" />
                  ) : (
                    <IconShare size={18} color="#1500FF" style={{ cursor: "pointer" }} />
                  )}
                </MantineUnstyledButton>
              </Tooltip>
            </>
          )}
          <Tooltip label="Notification Settings" position="top" withArrow>
            <Link
              href={user ? "/profile" : "/plans"}
              className={styles.notificationLink}
              onClick={(e) => e.stopPropagation()}
            >
              <IconBell size={18} color="#1500FF" style={{ cursor: "pointer" }} />
            </Link>
          </Tooltip>
        </Flex>
      </Flex>
      {!creating ? (
        <Combobox
          store={combobox}
          onOptionSubmit={(value) => {
            toggleSelected(Number(value));
            combobox.closeDropdown();
          }}
          position="bottom"
          width="target"
          offset={8}
          withinPortal={false}
          dropdownPadding={4}
        >
          <Combobox.Target targetType="button" withExpandedAttribute withKeyboardNavigation={false}>
            <MantineBox
              className={`${styles.titleContainer} ${signingIn ? styles.signInFlash : ""}`}
              onClick={toggleOpen}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  toggleOpen();
                }
              }}
              tabIndex={0}
              role="button"
              aria-label="Saved filters"
            >
              <MantineBox className={styles.title}>
                <MantineText span inherit className={styles.titleText}>
                  {signingIn
                    ? "SIGN IN TO SAVE FILTERS"
                    : activeSearch
                      ? activeSearch.search_filter.name
                      : "SAVED FILTERS"}
                </MantineText>
                {activeSearch && !signingIn && (
                  <MantineText span inherit className={styles.saveState} aria-live="polite">
                    <Tooltip
                      label="Save filter changes"
                      position="top"
                      withArrow
                      disabled={!hasUnsavedChanges}
                    >
                      <MantineUnstyledButton
                        type="button"
                        className={`${styles.saveStateButton} ${hasUnsavedChanges ? styles.saveStateActive : ""}`}
                        onClick={saveActiveChanges}
                        aria-label={`Save changes to ${activeSearch.search_filter.name}`}
                        aria-hidden={!hasUnsavedChanges}
                        tabIndex={hasUnsavedChanges ? 0 : -1}
                      >
                        SAVE
                      </MantineUnstyledButton>
                    </Tooltip>
                    <IconCheck
                      className={`${styles.savedIcon} ${!hasUnsavedChanges ? styles.saveStateActive : ""}`}
                      size={18}
                      stroke={2}
                      aria-label="Saved"
                      aria-hidden={hasUnsavedChanges}
                    />
                  </MantineText>
                )}
              </MantineBox>
              <ActionIcon
                className={styles.saveFilterButton}
                onClick={toggleCreating}
                aria-label="Add new filter"
                radius={0}
              >
                <PlusIcon color="currentColor" width={13} height={13} strokeWidth={2.5} />
              </ActionIcon>
            </MantineBox>
          </Combobox.Target>
          <Combobox.Dropdown className={styles.dropDown}>
            <ScrollArea.Autosize mah="18rem">
              <Combobox.Options>
                {savedFilters?.map((filter) => {
                  const isSelected = activeSearch?.search_filter.id == filter?.search_filter?.id;

                  return (
                    <Combobox.Option
                      key={filter?.search_filter?.id}
                      value={String(filter?.search_filter?.id)}
                      active={isSelected}
                      data-active-filter={isSelected}
                      className={styles.savedOption}
                    >
                      <MantineBox className={styles.savedOptionTitle}>
                        {isSelected && (
                          <IconCheck width="16px" height="16px" className={styles.optionCheck} />
                        )}
                        <MantineText span inherit className={styles.optionLabel}>
                          {filter?.search_filter?.name || "Unnamed Filter"}
                        </MantineText>
                      </MantineBox>
                      <ActionIcon
                        className={styles.deleteButton}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(filter.id);
                        }}
                        aria-label="Delete filter"
                        variant="transparent"
                      >
                        <BinIcon width="20" height="20" />
                      </ActionIcon>
                    </Combobox.Option>
                  );
                })}
                {user && !savedFilters?.length && (
                  <Combobox.Empty className={styles.noSaved}>No saved filters</Combobox.Empty>
                )}
              </Combobox.Options>
            </ScrollArea.Autosize>
          </Combobox.Dropdown>
        </Combobox>
      ) : (
        <MantineBox>
          <TextInput
            placeholder="FILTER NAME"
            onChange={onNameChange}
            className={styles.filterNameInput}
            radius={0}
          />
          <Flex justify="flex-end" gap="sm" mt="xs">
            <Button variant="secondary" size="xs" onClick={onSave}>
              SAVE
            </Button>
            <Button variant="secondary" size="xs" onClick={() => setCreating(false)}>
              CANCEL
            </Button>
          </Flex>
        </MantineBox>
      )}
      <RenameFilterModal
        opened={renameModalOpen}
        onClose={() => setRenameModalOpen(false)}
        onConfirm={(newName) => {
          if (activeSearch) renameSavedFilter(activeSearch, newName);
        }}
      />
    </MantineBox>
  );
};

export default SavedInput;
